
import { RoleApi } from "@/api/RoleApi";
import { UserApi } from "@/api/UserApi";
import { SECRET_INPUT_TYPE, SELECT_INPUT_TYPE, VALIDATION_ERROR_MESSAGE, Yup } from "@mavichpe/dynamic-forms";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Valida sucursal/terminal en formato Hacienda: solo dígitos, hasta `length`
 * posiciones y valor >= 1. Vacío es válido: significa "usar el valor por defecto".
 */
const isValidHaciendaId = (value: string | null | undefined, length: number): boolean => {
    const raw = `${value ?? ""}`.trim();
    if (!raw.length) return true;
    if (raw.length > length || !/^\d+$/.test(raw)) return false;
    return Number(raw) >= 1;
}

const CategoryFormConfig = {
    sections: [
        {
            hideBorderBottom: true,
            "fields": [
                [
                    {
                        "type": "hidden",
                        "name": "id",
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "name",
                        "label": "Nombre",
                        "isHorizontal": true,
                        "sizing": {
                            "sm": 6
                        },
                        "labelSizing": {
                            "sm": 2
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "email",
                        "label": "Email",
                        "isHorizontal": true,
                        "sizing": {
                            "sm": 4
                        },
                        "labelSizing": {
                            "sm": 2
                        },
                        "validation": Yup.string().required("El campo de email es obligatorio")
                            .test("is-valid-emails", "El correo electronico no es valido", (value) => {
                                if (!value) return false;
                                return EMAIL_REGEX.test(value)
                            })
                            // El correo es el username en Cognito: no puede repetirse. Se avisa
                            // aqui para no llegar a la lambda, que lo vuelve a validar.
                            .test("is-unique-email", "Ya existe un usuario con este correo electronico", async function (value) {
                                if (!value || !EMAIL_REGEX.test(value)) return true;
                                const { id } = this.parent as { id?: string | null };
                                try {
                                    const existing = await UserApi.findByEmail(value, id || undefined);
                                    return !existing;
                                } catch {
                                    // Si la consulta falla no se bloquea el formulario; la lambda decide.
                                    return true;
                                }
                            }),
                    },
                ],
                [
                    {
                        "type": SECRET_INPUT_TYPE,
                        "name": "password",
                        "label": "Contrasena",
                        "isHorizontal": true,
                        "sizing": {
                            "sm": 4
                        },
                        "labelSizing": {
                            "sm": 2
                        },
                        "validation": Yup.string().when("id", {
                            is: (id: unknown) => !id,
                            then: (s) => s.required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR)
                                .matches(
                                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
                                    "Debe tener al menos 8 caracteres y una mayúscula, una minúscula, un número y un símbolo"
                                ),
                            otherwise: (s) =>
                                s.notRequired().test(
                                    "password-optional-validation",
                                    "Debe tener al menos 8 caracteres, una mayúscula, una minúscula, un número y un símbolo",
                                    (value) => {
                                        // si el usuario no escribió nada, pasa sin validar
                                        if (!value) return true;

                                        // si escribió algo, validamos con regex
                                        return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/.test(value);
                                    }
                                ),
                        }),
                    },
                ],
                [
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "roleId",
                        "label": "Rol",
                        "optionsFunction": () => RoleApi.optionsList(),
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 4
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "storeId",
                        "label": "Número de Tienda",
                        "placeholder": "001",
                        "isHorizontal": true,
                        "sizing": {
                            "sm": 2
                        },
                        "labelSizing": {
                            "sm": 2
                        },
                        // Sucursal de Hacienda: 3 dígitos (001-999). Vacío = casa matriz (001).
                        "validation": Yup.string().nullable()
                            .test(
                                "is-valid-store",
                                "La tienda debe ser un número entre 1 y 999 (formato Hacienda: 001)",
                                (value) => isValidHaciendaId(value, 3)
                            ),
                    }
                ],
                [
                    {
                        "type": "text",
                        "name": "terminalId",
                        "label": "Número de Terminal",
                        "placeholder": "00001",
                        "isHorizontal": true,
                        "sizing": {
                            "sm": 2
                        },
                        "labelSizing": {
                            "sm": 2
                        },
                        // Terminal de Hacienda: 5 dígitos (00001-99999). Vacío = terminal por defecto (00001).
                        "validation": Yup.string().nullable()
                            .test(
                                "is-valid-terminal",
                                "La terminal debe ser un número entre 1 y 99999 (formato Hacienda: 00001)",
                                (value) => isValidHaciendaId(value, 5)
                            ),
                    },
                ],
            ]
        }
    ],
};


export default CategoryFormConfig;